export type PipelineMethodology = {
  pipelineCode: string;
  scraperCommand: string;
  sourceUrl: string;
  transport: 'http-csv' | 'http-json' | 'playwright' | 'http-form';
  cycle: string;
  segmentStrategy: string;
  fieldsFetched: string[];
  manualSteps: string[];
  notes?: string;
};

export const PIPELINE_METHODOLOGY: Record<string, PipelineMethodology> = {
  EGTS: {
    pipelineCode: 'EGTS',
    scraperCommand: 'node scripts/scrape-ebb-egts.js',
    sourceUrl: 'https://infopost.bhegts.com/egts/postings/capacity-operationally-available',
    transport: 'http-csv',
    cycle: 'Daily — posting cadence controlled by BHE GT&S; script picks the most-recently-posted CSV link on the listing page.',
    segmentStrategy:
      'Ingests every row in the CSV. Filters out rows where Design Capacity ≤ 0. No segment-number whitelist — all locations included.',
    fieldsFetched: [
      'Loc (segment number)',
      'Loc Name (segment name)',
      'Loc Zn (zone)',
      'Flow Ind (flow indicator)',
      'DC (Design Capacity, Dth/d)',
      'OPC (Operating Capacity, Dth/d)',
      'TSQ (Total Scheduled Quantity, Dth/d)',
      'OAC (Operationally Available Capacity, Dth/d)',
      'Eff Gas Day',
    ],
    manualSteps: [
      'Open https://infopost.bhegts.com/egts/postings/capacity-operationally-available',
      'Find the most recent CSV link on the page (first href matching /docs/egts/postings/…/egts-…csv)',
      'Download and open the CSV',
      'Confirm header row contains DC, OPC, TSQ, OAC columns',
      'Check Eff Gas Day column in row 2 for the gas day this posting covers',
      'Sum OAC column to compare against DB value in ebb_capacity for this gas_day',
    ],
    notes: 'Script exits 0 if the portal is down rather than crashing — check Railway logs for "Listing page fetch failed" to detect silent skips.',
  },

  TCO: {
    pipelineCode: 'TCO',
    scraperCommand: 'node scripts/scrape-ebb-tco.js',
    sourceUrl: 'https://www.tceconnects.com/infopost/ReportViewer.aspx?/InfoPost/OperationallyAvailableCapacity&pAssetNbr=51&rs:Format=CSV',
    transport: 'http-csv',
    cycle: 'Daily — single SSRS CSV export, no cycle selection needed. Reflects the latest posted cycle at time of request.',
    segmentStrategy:
      'Ingests every row returned by the SSRS export (pAssetNbr=51 = Columbia Gas Transmission). Filters rows where DesignCapacity ≤ 0.',
    fieldsFetched: [
      'Location (segment number)',
      'LocationName (segment name)',
      'Zone',
      'FlowInd (flow indicator)',
      'DesignCapacity (Dth/d)',
      'OperatingCapacity (Dth/d)',
      'TotalScheduledQuantity (Dth/d)',
      'OperationallyAvailableCapacity (Dth/d)',
      'EffGasDay',
    ],
    manualSteps: [
      'Open the URL above directly in a browser (or curl it)',
      'Expect a CSV download — if the browser shows HTML, the SSRS portal is in error state (known issue)',
      'Open the CSV; confirm EffGasDay column shows today\'s or yesterday\'s gas day',
      'Verify DesignCapacity and OperationallyAvailableCapacity columns are populated',
      'Sum OperationallyAvailableCapacity and compare to DB',
    ],
    notes: 'SSRS occasionally returns an HTML error page or redirect instead of CSV. Script detects this (response starts with "<") and exits 0 rather than crashing.',
  },

  MVP: {
    pipelineCode: 'MVP',
    scraperCommand: 'node scripts/scrape-ebb-mvp.js',
    sourceUrl: 'https://infopost.eqt.com/CustomerPortal/operationally-available/export?c__tspNumber=062498393&format=csv',
    transport: 'http-csv',
    cycle: 'Daily — single CSV export from EQT infopost portal for TSP 062498393 (Mountain Valley Pipeline). Reflects Point Capacity view only (v1); segment-direct ingest not implemented.',
    segmentStrategy:
      'Ingests every row in the CSV with DC > 0. Point Capacity view — individual delivery/receipt point locations. No Loc# whitelist.',
    fieldsFetched: [
      'Loc (location number)',
      'Loc Name',
      'Loc Zn (zone)',
      'Flow Ind',
      'DC (Design Capacity, Dth/d)',
      'OPC (Operating Capacity, Dth/d)',
      'TSQ (Total Scheduled Quantity, Dth/d)',
      'OAC (Operationally Available Capacity, Dth/d)',
      'Cycle Desc',
      'Eff Gas Day',
    ],
    manualSteps: [
      'Open the URL above in a browser or curl it',
      'Expect CSV — if you get HTML, the portal requires session authentication (known issue)',
      'Check Eff Gas Day column for the gas day',
      'Confirm DC, OPC, TSQ, OAC columns are numeric',
      'Compare summed OAC to DB value in ebb_capacity for this gas_day / pipeline_code = MVP',
    ],
    notes: 'Portal returns 404 or HTML on session timeout. Script exits 0 gracefully in those cases — check Railway logs for "MVP portal returned 404" to detect silent skips.',
  },

  TW: {
    pipelineCode: 'TW',
    scraperCommand: 'node scripts/scrape-ebb-tw.js',
    sourceUrl: 'https://pipelines.energytransfer.com/ipost/capacity/operationally-available?asset=TW',
    transport: 'http-json',
    cycle: 'Daily — script first calls the cycle API (get-all-cycle?asset=TW) to get the highest-displayOrder cycle code, then fetches CSV for that cycle. Reflects the latest posted cycle at request time.',
    segmentStrategy:
      'Fetches all locations (searchType=ALL, locType=ALL, locZone=ALL, max=ALL) from Energy Transfer iPost. Approximately 299 receipt/delivery locations. Filters rows where DC ≤ 0.',
    fieldsFetched: [
      'Loc (location number)',
      'Loc Name',
      'Loc Zn (zone)',
      'Flow Ind',
      'Loc Purp Desc (location purpose)',
      'DC (Design Capacity, Dth/d)',
      'OPC (Operating Capacity, Dth/d)',
      'TSQ (Total Scheduled Quantity, Dth/d)',
      'OAC (Operationally Available Capacity, Dth/d)',
    ],
    manualSteps: [
      'Open https://pipelines.energytransfer.com/ipost/capacity/operationally-available?asset=TW',
      'Select the latest cycle from the cycle dropdown (script picks highest displayOrder automatically)',
      'Export to CSV (f=csv parameter) or compare directly in the UI',
      'Verify DC, OPC, TSQ, OAC columns match expectation',
      'Gas day is embedded in the cycle metadata, not a CSV column — check cycle.display for the date',
    ],
    notes: 'The cycle-discovery step is a separate JSON API call before the CSV fetch. Raw CSV snapshots are saved to data/snapshots/ebb/TW/<gas_day>/ for audit.',
  },

  EPNG: {
    pipelineCode: 'EPNG',
    scraperCommand: 'node scripts/scrape-epng-ebb.js --code=EPNG',
    sourceUrl: 'https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=EPNG',
    transport: 'playwright',
    cycle: 'Daily — KM publishes once per gas day cycle. The page shows "Gas Day" and "Cycle" in the Excel metadata rows (row 2: [blank, blank, gas_day, cycle_name]).',
    segmentStrategy:
      'Iterates every segment row in the downloaded Excel. No Loc# whitelist. Rows with Design Capacity ≤ 0 are skipped. After insert, EPNG capacity points are geocoded by segment_name using a static EPNG_GEOCODES lookup table.',
    fieldsFetched: [
      'Loc (Segment) — segment number',
      'Loc Name (Segment) — segment name',
      'Loc Zn (zone)',
      'Flow Ind',
      'Design Capacity (Dth/d)',
      'Operating Capacity (Dth/d)',
      'Total Scheduled Quantity (Dth/d)',
      'Operationally Available Capacity (Dth/d)',
    ],
    manualSteps: [
      'Open https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=EPNG',
      'Page loads with a blank data panel — click the "Retrieve" button (scraper does this automatically)',
      'Wait for the segment grid to render (~5–15 s)',
      'Select "EXCEL" in the Download format dropdown',
      'Click "Download" — save the .xlsx file',
      'Open the Excel; row 2 columns C/D contain Gas Day and Cycle name',
      'Header row (containing "Design Capacity") is within the first 10 rows — find it and read DC, Operating Capacity, Total Scheduled Quantity, Operationally Available Capacity',
    ],
    notes: 'Playwright required — the Retrieve button triggers a POST that is not reachable via plain HTTP. Failure trace is saved to /tmp/ebb-EPNG-failure-*.zip for diagnosis. XLSX snapshots saved to data/snapshots/ebb/EPNG/<gas_day>/.',
  },

  GCX: {
    pipelineCode: 'GCX',
    scraperCommand: 'node scripts/scrape-epng-ebb.js --code=GCX',
    sourceUrl: 'https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=GCX',
    transport: 'playwright',
    cycle: 'Daily — same KM pipeline2 portal as EPNG; cycle name embedded in Excel metadata row 2.',
    segmentStrategy:
      'Same as EPNG: every segment row from the Excel download, DC > 0 filter, no Loc# whitelist. No EPNG-specific geocoding step for GCX.',
    fieldsFetched: [
      'Loc (Segment)',
      'Loc Name (Segment)',
      'Loc Zn',
      'Flow Ind',
      'Design Capacity (Dth/d)',
      'Operating Capacity (Dth/d)',
      'Total Scheduled Quantity (Dth/d)',
      'Operationally Available Capacity (Dth/d)',
    ],
    manualSteps: [
      'Open https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=GCX',
      'Click "Retrieve" — blank panel until this is clicked',
      'Wait for segment grid to render',
      'Select EXCEL in the Download dropdown, click Download',
      'Row 2 of the Excel contains Gas Day (col C) and Cycle (col D)',
      'Locate header row containing "Design Capacity" — read capacity columns',
    ],
    notes: 'Shares scraper binary with EPNG/PHP/NGPL/TGP. Playwright required. Failure trace at /tmp/ebb-GCX-failure-*.zip.',
  },

  PHP: {
    pipelineCode: 'PHP',
    scraperCommand: 'node scripts/scrape-epng-ebb.js --code=PHP',
    sourceUrl: 'https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=PHP',
    transport: 'playwright',
    cycle: 'Daily — same KM pipeline2 portal; cycle embedded in Excel row 2.',
    segmentStrategy:
      'Same as EPNG/GCX: all segment rows with DC > 0, no Loc# filter, no geocoding step.',
    fieldsFetched: [
      'Loc (Segment)',
      'Loc Name (Segment)',
      'Loc Zn',
      'Flow Ind',
      'Design Capacity (Dth/d)',
      'Operating Capacity (Dth/d)',
      'Total Scheduled Quantity (Dth/d)',
      'Operationally Available Capacity (Dth/d)',
    ],
    manualSteps: [
      'Open https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=PHP',
      'Click "Retrieve" — page will be blank until clicked',
      'Wait for segment grid',
      'Download as EXCEL, open file',
      'Row 2 C/D = Gas Day / Cycle; find "Design Capacity" header row',
    ],
    notes: 'Shares scraper binary with EPNG/GCX/NGPL/TGP. Playwright required.',
  },

  NGPL: {
    pipelineCode: 'NGPL',
    scraperCommand: 'node scripts/scrape-epng-ebb.js --code=NGPL',
    sourceUrl: 'https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=NGPL',
    transport: 'playwright',
    cycle: 'Daily — same KM pipeline2 portal; cycle embedded in Excel row 2.',
    segmentStrategy:
      'Same as EPNG/GCX/PHP: all segment rows with DC > 0, no Loc# filter.',
    fieldsFetched: [
      'Loc (Segment)',
      'Loc Name (Segment)',
      'Loc Zn',
      'Flow Ind',
      'Design Capacity (Dth/d)',
      'Operating Capacity (Dth/d)',
      'Total Scheduled Quantity (Dth/d)',
      'Operationally Available Capacity (Dth/d)',
    ],
    manualSteps: [
      'Open https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=NGPL',
      'Click "Retrieve"',
      'Wait for segment grid to render',
      'Download as EXCEL',
      'Row 2 C/D = Gas Day / Cycle',
      'Find "Design Capacity" header row; read DC, Operating Capacity, TSQ, OAC columns',
    ],
    notes: 'Shares scraper binary with EPNG/GCX/PHP/TGP. Playwright required.',
  },

  TGP: {
    pipelineCode: 'TGP',
    scraperCommand: 'node scripts/scrape-epng-ebb.js --code=TGP',
    sourceUrl: 'https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=TGP',
    transport: 'playwright',
    cycle: 'Daily — same KM pipeline2 portal; cycle embedded in Excel row 2.',
    segmentStrategy:
      'Same as other KM codes: all segment rows with DC > 0, no Loc# filter.',
    fieldsFetched: [
      'Loc (Segment)',
      'Loc Name (Segment)',
      'Loc Zn',
      'Flow Ind',
      'Design Capacity (Dth/d)',
      'Operating Capacity (Dth/d)',
      'Total Scheduled Quantity (Dth/d)',
      'Operationally Available Capacity (Dth/d)',
    ],
    manualSteps: [
      'Open https://pipeline2.kindermorgan.com/Capacity/OpAvailSegment.aspx?code=TGP',
      'Click "Retrieve"',
      'Wait for segment grid',
      'Download as EXCEL',
      'Row 2 C/D = Gas Day / Cycle',
      'Find "Design Capacity" header; read capacity columns',
    ],
    notes: 'Shares scraper binary with EPNG/GCX/PHP/NGPL. Playwright required.',
  },

  TE: {
    pipelineCode: 'TE',
    scraperCommand: 'node scripts/scrape-ebb-tetlp.js',
    sourceUrl: 'https://rtba.enbridge.com/InformationalPosting/Default.aspx?bu=TE&Type=OA',
    transport: 'http-form',
    cycle: 'Daily, multiple cycles (Timely, Evening, Intraday). Script downloads the latest-posted cycle by following an ASP.NET __doPostBack to the "Downloadable Format" CSV link. Cycle code is derived from Cycle_Desc column prefix (e.g., TIMELY, INTRDY).',
    segmentStrategy:
      'Ingests all rows where TSP_Name contains "TX EAST" or "TEXAS EAST" (pipeline-fidelity guard). All location rows with Total_Design_Capacity > 0 included. No Loc# filter. For utilization write-back: East-Texas pipeline segments (lon -95.5 to -93.0, lat 29.5 to 33.5) get ETX-zone utilization rate; all other TE segments get system-wide rate. Rows with utilization_source = "manual" are never overwritten.',
    fieldsFetched: [
      'Loc (location number)',
      'Loc_Name',
      'Loc_Zn (zone: ETX, STX, WLA, 1, 2, 3, ELA, etc.)',
      'Flow_Ind_Desc (flow indicator)',
      'Total_Design_Capacity (Dth/d)',
      'Operating_Capacity (Dth/d)',
      'Total_Scheduled_Quantity (Dth/d)',
      'Operationally_Available_Capacity (Dth/d)',
      'Cycle_Desc (e.g. INTRDYC_2026-05-03_1235)',
      'Eff_Gas_Day',
      'TSP_Name (pipeline-fidelity check)',
    ],
    manualSteps: [
      'Open https://rtba.enbridge.com/InformationalPosting/Default.aspx?bu=TE&Type=OA',
      'Locate the "Downloadable Format" link and click it — downloads a CSV named TE_OA_<YYYYMMDD>_<CYCLE>_<date>_<time>.csv',
      'Open the CSV; verify TSP_Name column = "TX EAST TRAN"',
      'Confirm Eff_Gas_Day and Cycle_Desc columns reflect the expected gas day',
      'For East-Texas corridor audit: filter Loc_Zn = "ETX" and sum Operating_Capacity and Total_Scheduled_Quantity to derive ETX-zone utilization',
      'Compare summed Operationally_Available_Capacity to DB value in ebb_capacity',
    ],
    notes: 'The ASP.NET download uses a ViewState POST chain (GET landing page → extract __VIEWSTATE/__EVENTVALIDATION → POST with __EVENTTARGET). No Playwright needed — plain HTTP. ETX-zone utilization (~731 location rows) is written separately from system-wide to accurately reflect East-Texas corridor conditions. Storage-related flow indicators ("Storage Withdrawal"/"Storage Injection") are truncated to 10-char codes to fit the flow_direction column.',
  },

  EQT: {
    pipelineCode: 'EQT',
    scraperCommand: 'node scripts/scrape-ebb-equitrans.js',
    sourceUrl: 'https://infopost.eqt.com/CustomerPortal/operationally-available/export?c__tspNumber=189569585&format=csv',
    transport: 'http-csv',
    cycle: 'Daily — single CSV export from EQT infopost portal for TSP 189569585 (Equitrans L.P.). Cycle name read from Cycle Desc column in the CSV.',
    segmentStrategy:
      'Ingests every row with DC > 0. No Loc# whitelist. All Point Capacity view locations included.',
    fieldsFetched: [
      'Loc (location number)',
      'Loc Name',
      'Loc Zn (zone)',
      'Flow Ind',
      'DC (Design Capacity, Dth/d)',
      'OPC (Operating Capacity, Dth/d)',
      'TSQ (Total Scheduled Quantity, Dth/d)',
      'OAC (Operationally Available Capacity, Dth/d)',
      'Cycle Desc',
      'Eff Gas Day',
    ],
    manualSteps: [
      'Open the URL above in a browser or curl it',
      'If you get HTML, the portal requires session authentication (known issue — exits gracefully)',
      'Open the CSV; check Eff Gas Day for the gas day',
      'Confirm DC, OPC, TSQ, OAC columns are numeric',
      'Sum OAC column and compare to DB value in ebb_capacity for pipeline_code = EQT',
    ],
    notes: 'Structurally identical to MVP scraper (same EQT portal, different TSP number). Portal returns 404 or HTML when session expired; script exits 0 in those cases.',
  },

  GS: {
    pipelineCode: 'GS',
    scraperCommand: 'node scripts/scrape-ebb-bwp.js',
    sourceUrl: 'https://www.gasquest.com/capacity/operationally-available (TSP=1, Gulf South)',
    transport: 'http-json',
    cycle: 'Daily, multiple cycles. Script calls the Boardwalk infopost JSON API (POST /infopost/infopostdetails with tspId=1, pageSize=60) to list up to 60 recent postings sorted descending, then selects the first match. A --date= or --cycle= filter can constrain selection.',
    segmentStrategy:
      'Ingests all rows where TSP = 078444247 (Gulf South pipeline-fidelity guard). Rows with notAvailReason set are excluded from utilization aggregation but still inserted. LineCode column (e.g. 125, 325, 425) is stored in ebb_capacity.zone for line-level attribution. segment_number = LineCode; segment_name = "<LineCode> · <Loc> · <Loc Name>".',
    fieldsFetched: [
      'TSP (pipeline-fidelity check — must equal 078444247)',
      'TSP Name',
      'LineCode (line-level grouping: 125, 325, 425, etc.)',
      'Loc (location number)',
      'Loc Name',
      'Loc Zn (zone)',
      'Loc Purp Desc',
      'Flow Ind',
      'Design Capacity (Dth/d)',
      'Operating Capacity (Dth/d)',
      'Total Scheduled Quantity (Dth/d)',
      'Operationally Available Capacity (Dth/d)',
      'Quantity Not Available Reason',
      'Effective Gas Day (YYYYMMDD format)',
    ],
    manualSteps: [
      'Open https://www.gasquest.com/capacity/operationally-available in a browser',
      'Select TSP = Gulf South (tspId=1) and the latest posting from the list',
      'Click the CSV download link for the selected posting',
      'The portal returns a base64-encoded CSV — browser download will decode it automatically',
      'Open the CSV; confirm TSP column = 078444247 throughout',
      'Check Effective Gas Day column (format YYYYMMDD)',
      'Filter to rows where Quantity Not Available Reason is empty for utilization calc',
      'Sum Design Capacity and compare total to DB',
    ],
    notes: 'The portal API returns base64-encoded CSV (not raw CSV) — manual download via browser handles this transparently. BWP occasionally emits double-spaces in the "Operationally  Available Capacity" header; scraper handles this with a normalized header match. Metadata (locPurp, qti, it, notAvailReason, postingId) is stored in ebb_capacity.metadata JSONB column.',
  },
};
